import { FC, useContext, useEffect, useState } from 'react';
import styled, { createGlobalStyle, css, keyframes } from 'styled-components';

import ContactUsButton from '@/components/ContactUsButton/ContactUsButton';
import Container from '@/components/Container/Container';
import HamburgerMenu from '@/components/HamburgerMenu/HamburgerMenu';
import LanguageDropdown from '@/components/LanguageDropdown/LanguageDropdown';
import LocalizedLink from '@/components/LocalizedLink/LocalizedLink';
import Logo from '@/components/Logo/Logo';
import HubspotForm from '@/components/Modal/HubspotForm';
import HubspotFormModal from '@/components/Modal/HubspotFormModal';
import NavigationDropdown from '@/components/NavigationDropdown/NavigationDropdown';
import {
	HUBSPOT_FORM_IDS,
	HUBSPOT_FORM_TITLES,
	STRAPI_LANGUAGES,
} from '@/lib/constants';
import { GlobalContext } from '@/pages/_app';

import ArrowIcon from './ArrowIcon';

const OverflowBody = createGlobalStyle`
  body, html {
    position: relative;
    overflow: hidden !important;
  }
`;

const GlobalStylesPrerendered = createGlobalStyle`
  .hamburger-menu-mobile {
    transition: 0s;
    visibility: hidden;
    transform: translateY(-100%);
  }
`;

interface Props {
	lang: string;
}

const Navigation: FC<Props> = ({ lang }) => {
	const { formId, navigation } = useContext(GlobalContext);
	const [data, setData] = useState<any>(null);
	const [openedHamburger, changeHamburgerOpenedStatus] = useState(false);
	const [currentFormId, setCurrentFormId] = useState<string | null>(null);

	const openModal = (formId: string) => setCurrentFormId(formId);

	const closeModal = () => setCurrentFormId(null);

	const toggleMenu = () => changeHamburgerOpenedStatus(!openedHamburger);

	useEffect(() => {
		if (!navigation) return;

		const localeVersion =
			navigation.locale === STRAPI_LANGUAGES[lang]
				? navigation
				: navigation.localizations?.find(
						(item) => item.locale === STRAPI_LANGUAGES[lang],
					);

		setData(localeVersion);
	}, [lang, navigation]);

	useEffect(() => {
		if (!formId) return;

		setCurrentFormId(formId);
	}, [formId]);

	return (
		<>
			<GlobalStylesPrerendered />

			<NavWrapper>
				<Container>
					<Row>
						<Left>
							<Logo logo={data?.logo?.url} />
						</Left>

						<Center>
							{data?.list?.map((item, index) => (
								<MenuItem
									key={`navigation_list_${index}`}
									$disable={item.disable}
								>
									<LocalizedLink href={item.link}>{item.name}</LocalizedLink>

									{item?.dropdownItem?.length > 0 && (
										<>
											<ArrowIcon />

											<DropdownWrapper>
												<NavigationDropdown data={item.dropdownItem} />
											</DropdownWrapper>
										</>
									)}
								</MenuItem>
							))}
						</Center>

						<Right>
							<LanguageDropdown />

							<ContactUsButton
								data={data?.contactUsButton}
								handleClick={() => {
									const formId =
										lang === 'ua'
											? HUBSPOT_FORM_IDS.CONTACT_US_UA
											: lang === 'ru'
												? HUBSPOT_FORM_IDS.CONTACT_US_RU
												: HUBSPOT_FORM_IDS.MAIN;

									openModal(formId);
								}}
							/>
						</Right>

						<HamburgerMobileIcon onClick={toggleMenu}>
							{openedHamburger ? (
								<img src="/images/icons/close.svg" alt="Close" />
							) : (
								<img src="/images/icons/open.svg" alt="Open" />
							)}
						</HamburgerMobileIcon>
					</Row>
				</Container>
			</NavWrapper>

			{openedHamburger ? <OverflowBody /> : null}

			<HamburgerMenu
				lang={lang}
				data={data?.list}
				openedHamburger={openedHamburger}
			/>

			{currentFormId ? (
				<HubspotFormModal handleClose={closeModal}>
					<h2>{HUBSPOT_FORM_TITLES.MAIN}</h2>

					<HubspotForm formId={currentFormId} handleClose={closeModal} />
				</HubspotFormModal>
			) : null}
		</>
	);
};

const DropdownWrapper = styled.div`
	position: absolute;
	width: 230px;
	top: 32px;
	left: -47px;
	opacity: 0;
	visibility: hidden;
	transition: 0.1s;
	z-index: 2000;
`;

const showUp = keyframes`
  0% {
    transform: translateY(-100px);
  }
  
  100% {
    transform: translateY(0);
  }

`;

const NavWrapper = styled.div`
	border-bottom: 1px solid rgb(101, 92, 172, 0.1);
	position: fixed;
	width: 100%;
	top: 0;
	left: 0;
	z-index: 2500;
	background-color: var(--color-white);
	padding: 15px 0;
	animation: ${showUp} 0.4s 0.2s 1 forwards;
	transform: translateY(-100px);

	@media (max-width: 900px) {
		backdrop-filter: none;
		border-bottom: 1px solid rgb(101, 92, 172, 0.2);
	}

	@media (max-width: 600px) {
		padding: 7px 0;
		border-bottom: 1px solid rgb(101, 92, 172, 0);
	}
`;

const Row = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const Left = styled.div`
	margin-right: 5px;
`;

const MenuItem = styled.div<{ $disable?: boolean }>`
	margin-left: 30px;
	margin-right: 30px;
	transition: 0.5s;
	pointer-events: auto;
	display: flex;
	align-items: center;
	cursor: pointer;
	position: relative;

	${({ $disable }) =>
		$disable &&
		css`
			& > a {
				pointer-events: none;
			}
		`};

	@media (max-width: 1380px) {
		margin-left: 23px;
		margin-right: 23px;
	}

	@media (max-width: 1307px) {
		margin-left: 15px;
		margin-right: 15px;
	}

	@media (max-width: 1170px) {
		margin-left: 9px;
		margin-right: 9px;
	}

	svg {
		margin-left: 10px;

		@media (max-width: 1150px) {
			margin-left: 6px;
		}

		@media (max-width: 1120px) {
			margin-left: 5px;
		}
	}

	&:hover {
		opacity: 1 !important;
		transition: 0s;

		${DropdownWrapper} {
			opacity: 1;
			visibility: visible;
		}

		div {
			color: var(--color-purple);
		}

		svg path {
			stroke: var(--color-purple);
		}
	}

	div {
		text-decoration: none;
		font-family: var(--font-sans);
		font-size: 16px;
		line-height: 24px;
		color: var(--color-black);
		transition: 0.2s;
	}
`;

const Center = styled.div`
	display: flex;
	align-items: center;
	pointer-events: none;

	@media (max-width: 900px) {
		display: none;
	}

	&:hover {
		${MenuItem} {
			opacity: 0.3;
		}
	}
`;

const Right = styled.div`
	display: flex;
	align-items: center;

	@media (max-width: 900px) {
		display: none;
	}
`;

const HamburgerMobileIcon = styled.div`
	width: 50px;
	height: 50px;
	display: none;
	background-color: var(--color-purple);
	background: linear-gradient(
		180deg,
		var(--color-purple) 0%,
		var(--color-purple-80) 100%
	);
	flex-direction: column;
	border-radius: 18px;
	cursor: pointer;

	img {
		margin: auto;
	}

	@media (max-width: 900px) {
		display: flex;
	}
`;

export default Navigation;
